import type { Range } from 'slate'

import { withoutLosingSelection } from '~publish/legacy/editor/helpers/withoutLosingSelection'

import type { BufferEditor } from '../../../BufferEditor/types.plate'
import type { WithPosition } from '../../../helpers/WithPosition'
import { findCharacterLimitPoint } from '../../character-limit-highlight'
import { FacebookMentionInputElement } from '../nodes/FacebookMentionInputElement'
import type { FacebookMentionEntityDetails } from '../nodes/FacebookMentionInputElement'

/**
 * Given an editor and a facebook mention entity,
 * find its location in the editor and insert
 * a FacebookMentionInputElement
 */
export const insertFacebookMention = (
  editor: BufferEditor,
  data: WithPosition<FacebookMentionEntityDetails>,
): Range | undefined => {
  const { start, length, ...mention } = data
  // For Facebook mentions, use UTF-16 indexing for consistency with backend and display components
  // This ensures mentions work correctly with complex emojis like 👨‍🎤

  // Since we're now using UTF-16 indices consistently, the start index should match directly
  const anchor = findCharacterLimitPoint(editor, start)
  const focus =
    anchor &&
    editor.api.after(anchor, {
      distance: length,
    })

  if (!anchor || !focus) return

  const { name } = mention

  // Only insert mention if text at saved indices matches expected mention text
  // NOTE - This will result in the mention not being picked up at save time
  // so it will essentially be forgotten.
  // Slack conversation: https://buffer.slack.com/archives/C05N3DJ5K7G/p1712242666626179
  const textAtIndices = editor.api.string({ anchor, focus })
  if (textAtIndices !== name) {
    return
  }

  const element = FacebookMentionInputElement.new(mention)

  withoutLosingSelection(editor, () => {
    editor.tf.insertNodes(element, {
      at: { anchor, focus },
    })
  })
}
